<script setup>
import { ref } from 'vue';
import { useAlert } from 'dashboard/composables';
import contactCrmAPI from 'dashboard/api/contactCrm';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  contactId: {
    type: [String, Number],
    required: true,
  },
  documents: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['refresh']);

const isLoading = ref(false);
const showForm = ref(false);
const selectedFile = ref(null);
const formData = ref({
  document_type: 'other',
  name: '',
  description: '',
  expiration_date: '',
});

const documentTypes = [
  { value: 'cpf', label: 'CPF' },
  { value: 'rg', label: 'RG' },
  { value: 'cnpj', label: 'CNPJ' },
  { value: 'cnh', label: 'CNH' },
  { value: 'contract', label: 'Contrato' },
  { value: 'proposal', label: 'Proposta' },
  { value: 'invoice', label: 'Nota Fiscal' },
  { value: 'receipt', label: 'Recibo' },
  { value: 'proof_of_address', label: 'Comprovante de Endereço' },
  { value: 'other', label: 'Outro' },
];

const formatDate = dateString => {
  if (!dateString) return '--';
  return new Date(dateString).toLocaleDateString('pt-BR');
};

const getDocumentTypeLabel = type => {
  return documentTypes.find(d => d.value === type)?.label || type;
};

const getStatusColor = status => {
  const colors = {
    pending: 'bg-amber-100 text-amber-700',
    verified: 'bg-emerald-100 text-emerald-700',
    rejected: 'bg-ruby-100 text-ruby-700',
    expired: 'bg-n-alpha-2 text-n-slate-11',
  };
  return colors[status] || colors.pending;
};

const getStatusLabel = status => {
  const labels = {
    pending: 'Pendente',
    verified: 'Verificado',
    rejected: 'Rejeitado',
    expired: 'Expirado',
  };
  return labels[status] || status;
};

const handleFileSelect = event => {
  const file = event.target.files[0];
  if (file) {
    selectedFile.value = file;
    if (!formData.value.name) {
      formData.value.name = file.name.split('.')[0];
    }
  }
};

const closeForm = () => {
  showForm.value = false;
  selectedFile.value = null;
  formData.value = {
    document_type: 'other',
    name: '',
    description: '',
    expiration_date: '',
  };
};

const handleSave = async () => {
  if (!selectedFile.value && !formData.value.name) {
    useAlert('Selecione um arquivo ou preencha o nome');
    return;
  }

  isLoading.value = true;
  try {
    if (selectedFile.value) {
      await contactCrmAPI.uploadDocument(
        props.contactId,
        selectedFile.value,
        formData.value
      );
    } else {
      await contactCrmAPI.createDocument(props.contactId, formData.value);
    }
    useAlert('Documento salvo com sucesso!');
    closeForm();
    emit('refresh');
  } catch (error) {
    useAlert('Erro ao salvar documento');
  } finally {
    isLoading.value = false;
  }
};

const handleDelete = async doc => {
  if (!confirm('Excluir este documento?')) return;
  try {
    await contactCrmAPI.deleteDocument(props.contactId, doc.id);
    useAlert('Documento excluído!');
    emit('refresh');
  } catch (error) {
    useAlert('Erro ao excluir documento');
  }
};

const handleVerify = async doc => {
  try {
    await contactCrmAPI.verifyDocument(props.contactId, doc.id);
    useAlert('Documento verificado!');
    emit('refresh');
  } catch (error) {
    useAlert('Erro ao verificar documento');
  }
};
</script>

<template>
  <div class="flex flex-col gap-6">
    <!-- Header -->
    <div class="flex items-center justify-between">
      <h3 class="text-base font-medium text-n-slate-12">
        Documentos ({{ documents.length }})
      </h3>
      <Button
        label="Upload Documento"
        icon="i-lucide-upload"
        size="sm"
        @click="showForm = true"
      />
    </div>

    <!-- Empty State -->
    <div
      v-if="documents.length === 0"
      class="flex flex-col items-center justify-center py-12 rounded-xl bg-n-alpha-1"
    >
      <span class="i-lucide-file-text size-12 text-n-slate-10 opacity-50" />
      <p class="mt-4 text-sm text-n-slate-11">Nenhum documento cadastrado</p>
    </div>

    <!-- Documents Grid -->
    <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      <div
        v-for="doc in documents"
        :key="doc.id"
        class="p-4 rounded-xl bg-n-solid-2 outline outline-1 outline-n-container"
      >
        <div class="flex items-start gap-3">
          <div
            class="flex-shrink-0 size-12 rounded-lg bg-n-alpha-2 flex items-center justify-center"
          >
            <span
              :class="{
                'i-lucide-file-text': doc.file_type === 'pdf',
                'i-lucide-image': doc.file_type === 'image',
                'i-lucide-file': !['pdf', 'image'].includes(doc.file_type),
              }"
              class="size-6 text-n-slate-11"
            />
          </div>
          <div class="flex-1 min-w-0">
            <p class="text-sm font-medium text-n-slate-12 truncate">
              {{ doc.name }}
            </p>
            <p class="text-xs text-n-slate-10">
              {{ getDocumentTypeLabel(doc.document_type) }}
            </p>
            <span
              class="inline-block mt-1.5 px-2 py-0.5 rounded-full text-xs font-medium"
              :class="getStatusColor(doc.verification_status)"
            >
              {{ getStatusLabel(doc.verification_status) }}
            </span>
          </div>
        </div>

        <div v-if="doc.expiration_date" class="mt-3 text-xs text-n-slate-10">
          Validade: {{ formatDate(doc.expiration_date) }}
          <span v-if="doc.expired" class="text-ruby-600 font-medium">
            (Expirado)
          </span>
          <span v-else-if="doc.expiring_soon" class="text-amber-600 font-medium">
            (Expirando)
          </span>
        </div>

        <div class="flex items-center gap-2 mt-4 pt-3 border-t border-n-weak">
          <a
            v-if="doc.file_url"
            :href="doc.file_url"
            target="_blank"
            class="px-2.5 py-1 text-xs font-medium text-woot-600 hover:bg-woot-500/10 rounded-lg transition-colors"
          >
            Visualizar
          </a>
          <button
            v-if="doc.verification_status === 'pending'"
            class="px-2.5 py-1 text-xs font-medium text-emerald-600 hover:bg-emerald-500/10 rounded-lg transition-colors"
            @click="handleVerify(doc)"
          >
            Verificar
          </button>
          <button
            class="px-2.5 py-1 text-xs font-medium text-ruby-600 hover:bg-ruby-500/10 rounded-lg transition-colors"
            @click="handleDelete(doc)"
          >
            Excluir
          </button>
        </div>
      </div>
    </div>

    <!-- Upload Form Modal -->
    <Teleport to="body">
      <div
        v-if="showForm"
        class="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      >
        <div
          class="absolute inset-0 bg-n-alpha-black5 backdrop-blur-sm"
          @click="closeForm"
        />
        <div
          class="relative w-full max-w-md overflow-hidden rounded-2xl bg-n-solid-2 shadow-xl outline outline-1 outline-n-container flex flex-col"
        >
          <!-- Header -->
          <div
            class="flex items-center justify-between px-6 py-4 border-b border-n-weak"
          >
            <h3 class="text-base font-medium text-n-slate-12">
              Upload de Documento
            </h3>
            <button
              class="size-8 rounded-lg flex items-center justify-center text-n-slate-11 hover:bg-n-alpha-2 transition-colors"
              @click="closeForm"
            >
              <span class="i-lucide-x size-5" />
            </button>
          </div>

          <!-- Content -->
          <div class="p-6 flex flex-col gap-4">
            <!-- File Upload -->
            <div class="flex flex-col gap-1.5">
              <label class="text-sm font-medium text-n-slate-11">Arquivo</label>
              <div
                class="border-2 border-dashed border-n-weak rounded-xl p-6 text-center hover:border-woot-500/50 transition-colors"
              >
                <input
                  id="file-upload"
                  type="file"
                  class="hidden"
                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                  @change="handleFileSelect"
                />
                <label for="file-upload" class="cursor-pointer">
                  <span class="i-lucide-cloud-upload size-8 text-n-slate-10 mx-auto" />
                  <p class="mt-2 text-sm text-n-slate-11">
                    {{
                      selectedFile
                        ? selectedFile.name
                        : 'Clique para selecionar um arquivo'
                    }}
                  </p>
                </label>
              </div>
            </div>

            <div class="flex flex-col gap-1.5">
              <label class="text-sm font-medium text-n-slate-11">Tipo</label>
              <select
                v-model="formData.document_type"
                class="w-full h-9 px-3 rounded-lg border border-n-weak bg-n-solid-2 text-n-slate-12 text-sm focus:border-woot-500 focus:outline-none transition-colors"
              >
                <option
                  v-for="type in documentTypes"
                  :key="type.value"
                  :value="type.value"
                >
                  {{ type.label }}
                </option>
              </select>
            </div>

            <div class="flex flex-col gap-1.5">
              <label class="text-sm font-medium text-n-slate-11">Nome</label>
              <input
                v-model="formData.name"
                type="text"
                class="w-full h-9 px-3 rounded-lg border border-n-weak bg-n-solid-2 text-n-slate-12 text-sm focus:border-woot-500 focus:outline-none transition-colors"
              />
            </div>

            <div class="flex flex-col gap-1.5">
              <label class="text-sm font-medium text-n-slate-11">
                Data de Validade
              </label>
              <input
                v-model="formData.expiration_date"
                type="date"
                class="w-full h-9 px-3 rounded-lg border border-n-weak bg-n-solid-2 text-n-slate-12 text-sm focus:border-woot-500 focus:outline-none transition-colors"
              />
            </div>

            <div class="flex flex-col gap-1.5">
              <label class="text-sm font-medium text-n-slate-11">Descrição</label>
              <textarea
                v-model="formData.description"
                rows="2"
                class="w-full px-3 py-2 rounded-lg border border-n-weak bg-n-solid-2 text-n-slate-12 text-sm focus:border-woot-500 focus:outline-none transition-colors resize-none"
              />
            </div>
          </div>

          <!-- Footer -->
          <div
            class="flex items-center justify-end gap-3 px-6 py-4 border-t border-n-weak"
          >
            <Button
              label="Cancelar"
              variant="ghost"
              color="slate"
              @click="closeForm"
            />
            <Button
              :label="isLoading ? 'Enviando...' : 'Enviar'"
              :is-loading="isLoading"
              :disabled="isLoading"
              @click="handleSave"
            />
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>
